// /src/lib/supabase.js
// Supabase client exports
//
// IMPORTANT: Always import from this file, not directly from @supabase/supabase-js
//
// Usage:
//   import { supabase } from '../lib/supabase';      // Protected client (default)
//   import { supabaseRaw } from '../lib/supabase';   // Raw client (auth operations only)
//   import { supabaseFetch } from '../lib/supabase'; // Direct fetch helper (reliable fallback)

// Import from protectedSupabase (all imports must be at top)
import { supabase, supabaseRaw } from './protectedSupabase';
import { authStorage } from './authStorage';

// Re-export protected client and raw client
export { supabase, supabaseRaw };

// Direct fetch helper for when Supabase JS client is unreliable
// This uses native fetch API which works when the client doesn't
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Single-flight guard for token refresh (see getAuthToken). Module-scoped so all
// concurrent callers share ONE in-flight refresh instead of racing. Supabase rotates
// refresh tokens, so two concurrent refreshes with the same token make the loser 400
// and wipe the session — the Android cold-start race that logged users out.
let refreshInFlight = null;

// ── Demo read target (Phase B1, docs/briefs/2026-09-18-phase-b-demo-routing-build.md) ──
// While an appointed demo admin is viewing /demo/:slug, GET reads of the tables that moved to the
// `demo` schema are sent with `Accept-Profile: demo`. Everything else — shared reference data
// (boundaries, translations, tag vocabulary, user areas…) and every rpc() — stays on `public`.
// Writes (Phase B2): a write that carries an id read from `demo`, made while the target is on, to a
// table and operation on DEMO_WRITE_ALLOWLIST, is sent with `Content-Profile: demo`. Every other
// write touching demo is REFUSED client-side (see resolveWriteTarget) — some tables (user_actions,
// smart_links, content_reports) have no FK on target_id, so a write sent to `public` would land
// silently carrying a demo id.
// App.js turns the target on and off at the same synchronous points it stamps activeContextRef.
// Design: docs/escalations/2026-09-18-phase-b1-schema-targeting-design.md (accepted 18 Sep 2026).
//
// Source of truth for this list: c_tables in supabase/migrations/20260918b_demo_schema_create.sql.
export const DEMO_SCHEMA_TABLES = new Set([
  'users', 'communities', 'articles', 'chats', 'community_members',
  'article_engagement', 'message_engagement', 'event_details', 'community_footprint',
  'group_relationships', 'group_links', 'smart_links', 'community_landing',
  'user_reputation', 'group_join_requests', 'user_actions'
]);

// Writes that may go to `demo` (Phase B2 — signed-in interaction on a demo page). Only these: each
// table here was checked for what `demo` lacks (it has no triggers at all, 20260918b) — for these
// the loss is rate limits and notifications, which a showcase should not have anyway.
// Deliberately absent, so refused: articles/event_details (composers — tier 4, not B2), smart_links
// (share links out of scope; demo links never resolve via /l/*), group_join_requests (a federation
// application or approval-required join would sit pending forever: its resolver, AdminGroup, is
// outside demo mode), and everything else. `communities` is update-only, and only member_count
// (the client-side twin of increment/decrement_member_count, src/data/demoReadService.js).
const DEMO_WRITE_ALLOWLIST = {
  insert: new Set(['chats', 'user_actions', 'article_engagement', 'message_engagement', 'community_members']),
  update: new Set(['chats', 'user_actions', 'communities']),
  delete: new Set(['chats', 'user_actions', 'community_members'])
};
const DEMO_COMMUNITIES_UPDATE_COLUMNS = new Set(['member_count']);

// Functions that are correct to call on `public` while viewing a demo (shared reference data or
// the viewer's own state). Any other rpc() while the target is on logs a development warning:
// a public function reading a moved table answers from `public` and returns nothing for demo ids.
// Any other rpc() whose parameters carry a demo id is refused (see rpc()).
const DEMO_SAFE_RPCS = new Set([
  'get_english_strings', 'get_translations', 'get_boundary_centroid',
  'get_smallest_boundary_centroid', 'resolve_point_to_boundary', 'get_boundaries_by_ids',
  'get_notifications', 'is_appointed_demo_admin', 'get_public_profiles', 'is_steward',
  'is_locality_admin', 'get_account_closure_status'
]);

// Rows read from `demo` while the target is on. App.js uses this to decide whether an item being
// opened came from the demo (stay) or from `public` (leave demo mode) — provenance, not view name.
const DEMO_PROVENANCE_TABLES = new Set(['communities', 'articles', 'chats']);

// ids: provenance for the current demo session (cleared on exit).
// seenIds: every id ever read from `demo` in this page session — NOT cleared on exit. Demo content
// can outlive demo mode in React state (e.g. Back after leaving for another view), so the write
// guard below checks this set too: no write carrying a demo id may ever reach `public`.
const demoRead = { active: false, ids: new Set(), seenIds: new Set() };

/**
 * Turn the demo read target on. Provenance starts empty on a fresh entry; a re-entry while already
 * on (App.js re-runs the demo route on every Back/Forward) keeps it, so ids already on screen do
 * not look foreign mid-reload.
 */
export const enterDemoRead = () => {
  if (!demoRead.active) demoRead.ids = new Set();
  demoRead.active = true;
};

/** Turn the demo read target off. */
export const exitDemoRead = () => {
  demoRead.active = false;
  demoRead.ids = new Set();
};

export const isDemoReadActive = () => demoRead.active;

/** True when this id was read from the `demo` schema during the current demo session. */
export const isDemoId = (id) => !!id && demoRead.ids.has(String(id));

/** True when this id was read from `demo` at any point in this page session (survives exit). */
export const isKnownDemoId = (id) => !!id && demoRead.seenIds.has(String(id));

/**
 * Should demo mode hold for this screen? Pure predicate, used by App.js on every view change.
 * Item details (article/event/chat) are guarded by provenance in their open handlers; a group feed
 * or landing page holds only for a group read from `demo` this session. The chat composer (Phase B2:
 * posting is a demo-page action) holds only when posting into, or editing a chat of, the demo.
 */
export const demoModeHoldsFor = ({ view, context, groupId, landingGroupId, chatGroupId, chatId }) => {
  if (['article', 'event', 'chat'].includes(view)) return true;
  if (view === 'feed') return context === 'groups' && isDemoId(groupId);
  if (view === 'group-landing-view') return isDemoId(landingGroupId);
  if (view === 'new-chat') return isDemoId(chatGroupId) || isDemoId(chatId);
  return false;
};

/**
 * The PostgREST profile (schema) a request to `table` should target, or null for the default
 * (`public`). An explicit options.schema always wins.
 */
export const resolveReadSchema = (table, explicitSchema) => {
  if (explicitSchema) return explicitSchema === 'public' ? null : explicitSchema;
  return demoRead.active && DEMO_SCHEMA_TABLES.has(table) ? 'demo' : null;
};

// Does a write's payload or filter (or an rpc's parameters) carry an id read from `demo`? (Shallow:
// values, and arrays of values, of the body object(s), eq filters and inFilter lists.)
const carriesKnownDemoId = (...parts) => {
  if (demoRead.seenIds.size === 0) return false;
  const hit = (v) => (Array.isArray(v) ? v.some(hit) : typeof v === 'string' && demoRead.seenIds.has(v));
  return parts.some(part => {
    if (!part) return false;
    const rows = Array.isArray(part) ? part : [part];
    return rows.some(row => row && typeof row === 'object' && Object.values(row).some(hit));
  });
};

const isAllowlistedDemoWrite = (op, table, payload) => {
  if (!DEMO_WRITE_ALLOWLIST[op]?.has(table)) return false;
  if (table !== 'communities') return true;
  const rows = Array.isArray(payload) ? payload : [payload];
  return rows.every(row => row && Object.keys(row).length > 0
    && Object.keys(row).every(col => DEMO_COMMUNITIES_UPDATE_COLUMNS.has(col)));
};

/**
 * Where a write goes: { schema: 'demo' }, { schema: null } (public), or { refused: true }.
 * Rules, in order (first match wins):
 *  1. an explicit schema other than 'public' or 'demo'                                → refused
 *  2. explicit 'demo' with the target off                                             → refused
 *  3. the write carries an id read from `demo` this session:
 *     a. target off (stale demo content still on screen after leaving demo mode)      → refused
 *     b. explicit 'public' (a demo id must never reach public)                        → refused
 *     c. table + operation on DEMO_WRITE_ALLOWLIST                                    → demo
 *     d. anything else (content_reports, smart_links, articles, group_join_blocks…)   → refused
 *  4. explicit 'demo' with no demo id (nothing to anchor it to the demo on screen)    → refused
 *  5. target on, one of the moved 16 tables, and not explicitly 'public'              → refused
 *  6. otherwise                                                                       → public
 * @param {'insert'|'update'|'delete'} op
 */
export const resolveWriteTarget = (op, table, explicitSchema, payload, ...filters) => {
  const REFUSED = { refused: true };
  if (explicitSchema && explicitSchema !== 'public' && explicitSchema !== 'demo') return REFUSED;
  if (explicitSchema === 'demo' && !demoRead.active) return REFUSED;
  if (carriesKnownDemoId(payload, ...filters)) {
    if (!demoRead.active || explicitSchema === 'public') return REFUSED;
    return isAllowlistedDemoWrite(op, table, payload) ? { schema: 'demo' } : REFUSED;
  }
  if (explicitSchema === 'demo') return REFUSED;
  if (demoRead.active && DEMO_SCHEMA_TABLES.has(table) && explicitSchema !== 'public') return REFUSED;
  return { schema: null };
};

// Returned, with no request sent, for a refused demo write and for an rpc() carrying a demo id.
const DEMO_REFUSED = { message: 'demo_refused', code: 'DEMO_REFUSED' };

/** True for the error the wrapper returns when it refuses a demo write or rpc (no request was sent). */
export const isDemoRefusal = (error) => error?.code === DEMO_REFUSED.code;

// ── Demo session guard (docs/briefs/2026-09-25-demo-read-expired-session.md + addendum 1) ──
// A request targeting `demo` must never fall back to the anon key. `anon` holds no privilege on the
// demo schema (20260918b: USAGE to authenticated/service_role only; all table privileges revoked
// from anon), so such a request can only ever come back "permission denied for schema demo" — which
// every caller swallows into [] or null, leaving the page silently empty with the watermark still up.
// Requests to `public` keep the anon fallback exactly as before: that is how a signed-out visitor
// reads the site, and nothing here may change it.
const DEMO_SESSION_EXPIRED = { message: 'demo_session_expired', code: 'DEMO_SESSION_EXPIRED' };

/**
 * True for the error returned when a demo-targeted request was not sent because the session is dead.
 * DELIBERATELY DISTINCT FROM isDemoRefusal: DEMO_REFUSED means "the demo rules forbid this call" —
 * permanent for that call, and correctly swallowed (a demo admin pressing Report, say). This means
 * "we would have served it, but your session is dead" — transient, fixed by signing in, and the one
 * failure that must reach the user. Sharing one code would make the notice fire on ordinary write
 * refusals or on neither.
 */
export const isDemoSessionExpired = (error) => error?.code === DEMO_SESSION_EXPIRED.code;

/**
 * Is this access token usable — present, decodable, and not yet expired?
 *
 * Not the same question as "is it non-null". getAuthToken() deliberately returns a NON-NULL but
 * possibly unusable token on FIVE branches, each of which would otherwise reach `demo` and fail.
 * The first three predate 2026-09-25; the last two arrived with ruling 2 of
 * docs/briefs/2026-09-25-single-token-refresher.md, which made a refused refresh stop meaning
 * "the session is dead":
 *   - an undecodable JWT ("try to use it anyway");
 *   - an expired token with no refresh token to exchange;
 *   - an expired token whose refresh hit a network/5xx error ("keep token and retry later") —
 *     which is precisely the laptop waking before the network is back;
 *   - a 400/401 carrying a code we do not recognise: refused is not proof of death, so the existing
 *     token is kept rather than the session cleared;
 *   - a 5xx/network failure on the single retry after a rotation collision.
 * One further branch returns a token that IS usable and is not a caller's concern: a token recovered
 * from storage because another refresher won the rotation race. isUsableAccessToken() is what
 * separates that case from the five above, which is why the recovery path calls it before returning.
 * No expiry margin: getAuthToken() has already tried to refresh by the time we see the token, so a
 * token that is still live will serve the request. Refusing one with seconds left would be a false
 * positive, and the cost of the race (one refused request, one notice, sign in again) is small.
 */
export const isUsableAccessToken = (token) => {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' && payload.exp > Math.floor(Date.now() / 1000);
  } catch (e) {
    return false;
  }
};

/**
 * The credential a request should carry, given the schema it targets. The criterion is the TARGET
 * SCHEMA, not the code path, and it is expressed here once rather than at each call site.
 *
 * Applies at the four sites that can carry a schema profile — query (Accept-Profile) and
 * update/insert/delete (Content-Profile). It deliberately does NOT apply to rpc(), which computes
 * no schema and sets no profile header (every function lives in `public`, and no demo RPCs exist
 * after 20260919_b3b), nor to storageUpload/storageList, which address the Storage API and carry no
 * schema profile at all.
 *
 * @param {string|null} schema - the resolved target schema ('demo', or null for public)
 * @param {string|null} authToken - whatever credential the call site would otherwise have used
 * @returns {{token: string}|{expired: true}}
 */
export const resolveRequestToken = (schema, authToken) => {
  if (schema === 'demo') {
    return isUsableAccessToken(authToken) ? { token: authToken } : { expired: true };
  }
  return { token: authToken || SUPABASE_KEY };
};

// Build the refusal AND tell the app, so the user learns the demo session died rather than seeing an
// empty page. The callers in between swallow errors into []/null by long-standing convention (out of
// scope to change here), so an event is the only way the signal reaches App.js. One event per refused
// request; App.js collapses them into one notice per page.
const demoSessionExpiredError = () => {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent('ournet:demo-session-expired'));
  }
  return DEMO_SESSION_EXPIRED;
};

const recordDemoProvenance = (table, rows) => {
  (Array.isArray(rows) ? rows : [rows]).forEach(row => {
    if (!row || !row.id) return;
    // Every row read from demo is remembered for the write guard, whatever the table.
    demoRead.seenIds.add(String(row.id));
    // Session provenance (what may stay open in demo mode) — groups, articles and chats.
    if (demoRead.active && DEMO_PROVENANCE_TABLES.has(table)) demoRead.ids.add(String(row.id));
  });
};

export const supabaseFetch = {
  /**
   * Direct REST API query to Supabase
   * @param {string} table - Table name
   * @param {object} options - Query options
   * @param {string} options.select - Fields to select (default: '*')
   * @param {object} options.eq - Equality filters { column: value }
   * @param {string} options.order - Order by column
   * @param {number} options.limit - Limit results
   * @param {boolean} options.single - Return single object instead of array
   * @param {object} options.neq - Inequality filters { column: value }
   * @param {object} options.like - Case-sensitive pattern filters { column: 'rate_*' }
   * @param {string[]} options.notNull - Columns that must not be NULL
   * @param {string} options.count - 'exact' to request a row count (returned as `count`)
   * @param {boolean} options.head - With count: fetch the count only, no rows (data is null)
   * @param {string} options.schema - 'public' or 'demo': overrides the demo read target
   * @returns {Promise<{data: any, error: any, count?: number}>}
   */
  async query(table, options = {}) {
    const { select = '*', eq = {}, neq = {}, is: isFilter = {}, notNull = [], inFilter = {}, like = {}, ilike = {}, or, order, limit, offset, single, count, head, schema: explicitSchema } = options;
    const schema = resolveReadSchema(table, explicitSchema);

    // Build URL params
    const params = new URLSearchParams();
    params.append('select', select);

    // Add equality filters
    Object.entries(eq).forEach(([key, value]) => {
      params.append(key, `eq.${value}`);
    });

    // Add inequality filters
    Object.entries(neq).forEach(([key, value]) => {
      params.append(key, `neq.${value}`);
    });

    // Add 'is' filters (PostgREST IS — for NULL/true/false/unknown, which 'eq' cannot express)
    Object.entries(isFilter).forEach(([key, value]) => {
      params.append(key, `is.${value === null ? 'null' : value}`);
    });

    // Add NOT NULL filters (for arrays like ['community_id'])
    notNull.forEach(key => {
      params.append(key, 'not.is.null');
    });

    // Add 'in' filters (for arrays like { id: [1,2,3] })
    Object.entries(inFilter).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        params.append(key, `in.(${values.join(',')})`);
      }
    });

    // Add 'like' filters (case-sensitive pattern; PostgREST reads * as %)
    Object.entries(like).forEach(([key, pattern]) => {
      params.append(key, `like.${pattern}`);
    });

    // Add 'ilike' filters (for case-insensitive pattern matching)
    Object.entries(ilike).forEach(([key, pattern]) => {
      params.append(key, `ilike.${pattern}`);
    });

    // Add 'or' filter (PostgREST OR across columns, e.g. "email.ilike.*term*,display_name.ilike.*term*")
    if (or) {
      params.append('or', `(${or})`);
    }

    // Add order
    if (order) {
      params.append('order', order);
    }

    // Add limit
    if (limit) {
      params.append('limit', limit);
    }

    // Add offset
    if (offset) {
      params.append('offset', offset);
    }

    try {
      // Use user's JWT token if available for RLS, fall back to anon key — except for a demo-targeted
      // request, which is not sent at all without a usable session (see resolveRequestToken).
      const authToken = await this.getAuthToken();
      const credential = resolveRequestToken(schema, authToken);
      if (credential.expired) return { data: null, error: demoSessionExpiredError() };
      const token = credential.token;

      const headers = {
        'apikey': SUPABASE_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      if (schema) headers['Accept-Profile'] = schema;
      if (count) headers['Prefer'] = `count=${count}`;

      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/${table}?${params.toString()}`,
        {
          method: head ? 'HEAD' : 'GET',
          headers
        }
      );

      if (!response.ok) {
        const errorText = head ? '' : await response.text();
        return { data: null, error: { message: errorText, status: response.status } };
      }

      // Content-Range is "0-9/42" or "*/42" when a count was requested.
      const total = count ? parseInt((response.headers.get('Content-Range') || '').split('/')[1], 10) : undefined;
      const countResult = count ? { count: Number.isNaN(total) ? null : total } : {};

      if (head) {
        return { data: null, error: null, ...countResult };
      }

      const data = await response.json();
      if (schema === 'demo') recordDemoProvenance(table, data);

      // If single requested, return first item or null
      if (single) {
        return { data: Array.isArray(data) ? data[0] || null : data, error: null, ...countResult };
      }

      return { data, error: null, ...countResult };
    } catch (error) {
      console.error('supabaseFetch error:', error);
      return { data: null, error: { message: error.message } };
    }
  },

  /**
   * Call an RPC function
   * @param {string} fn - Function name
   * @param {object} params - Function parameters
   * @returns {Promise<{data: any, error: any}>}
   */
  async rpc(fn, params = {}) {
    // Every function lives in `public` and runs there. A demo id in its parameters is always wrong:
    // a read returns nothing for it, and a write would act on public (or silently on no row), so it
    // is refused without a request. Callers already treat an error as "nothing" / "not allowed".
    if (!DEMO_SAFE_RPCS.has(fn) && carriesKnownDemoId(params)) {
      return { data: null, error: DEMO_REFUSED };
    }
    if (demoRead.active && !DEMO_SAFE_RPCS.has(fn) && process.env.NODE_ENV === 'development') {
      console.warn(`⚠️ Demo read target is on, but rpc('${fn}') always runs on public — demo ids get no answer from it.`);
    }
    try {
      // Use user's JWT token if available for RLS, fall back to anon key
      const authToken = await this.getAuthToken();
      const token = authToken || SUPABASE_KEY;

      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/rpc/${fn}`,
        {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(params)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        return { data: null, error: { message: errorText, status: response.status } };
      }

      // Void-returning functions (RETURNS void) reply with an empty body. Calling
      // response.json() on nothing throws "Unexpected end of JSON input", which would
      // look like a failure even though the RPC succeeded. Parse only when there's a body.
      const text = await response.text();
      const data = text ? JSON.parse(text) : null;
      return { data, error: null };
    } catch (error) {
      console.error('supabaseFetch RPC error:', error);
      return { data: null, error: { message: error.message } };
    }
  },

  /**
   * Update records in a table
   * @param {string} table - Table name
   * @param {object} data - Data to update
   * @param {object} options - Update options
   * @param {object} options.eq - Equality filters { column: value }
   * @param {string} options.authToken - Optional auth token for RLS
   * @returns {Promise<{data: any, error: any}>}
   */
  async update(table, data, options = {}) {
    const { eq = {}, inFilter = {}, authToken: explicitToken, schema: explicitSchema } = options;
    const target = resolveWriteTarget('update', table, explicitSchema, data, eq, inFilter);
    if (target.refused) return { data: null, error: DEMO_REFUSED };

    // Build URL params for filters
    const params = new URLSearchParams();
    Object.entries(eq).forEach(([key, value]) => {
      params.append(key, `eq.${value}`);
    });
    Object.entries(inFilter).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        params.append(key, `in.(${values.join(',')})`);
      }
    });

    // Never issue a PATCH with zero filters — PostgREST would update every row in the table.
    if (params.toString() === '') {
      return { data: null, error: { message: 'update() requires at least one eq/inFilter condition' } };
    }

    // Use user's JWT token if available for RLS, fall back to anon key — except for a demo-targeted
    // request, which is not sent at all without a usable session (see resolveRequestToken). An
    // explicitly-passed authToken is tested too: it is the credential that would actually be sent.
    const authToken = explicitToken || await this.getAuthToken();
    const credential = resolveRequestToken(target.schema, authToken);
    if (credential.expired) return { data: null, error: demoSessionExpiredError() };
    const token = credential.token;

    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/${table}?${params.toString()}`,
        {
          method: 'PATCH',
          headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation',
            ...(target.schema ? { 'Content-Profile': target.schema } : {})
          },
          body: JSON.stringify(data)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        return { data: null, error: { message: errorText, status: response.status } };
      }

      const result = await response.json();
      if (target.schema === 'demo') recordDemoProvenance(table, result);
      return { data: Array.isArray(result) ? result[0] : result, error: null };
    } catch (error) {
      console.error('supabaseFetch update error:', error);
      return { data: null, error: { message: error.message } };
    }
  },

  /**
   * Insert records into a table
   * @param {string} table - Table name
   * @param {object|array} data - Data to insert (single object or array)
   * @param {object} options - Insert options
   * @param {string} options.authToken - Optional auth token for RLS
   * @param {boolean} options.returnMinimal - Use return=minimal (no read-back)
   * @param {boolean} options.ignoreDuplicates - Use resolution=ignore-duplicates (silent no-op on unique conflict)
   * @param {boolean} options.upsert - Use resolution=merge-duplicates (INSERT ... ON CONFLICT DO UPDATE)
   * @param {string} options.onConflict - Conflict target column(s) for upsert (e.g. 'endpoint'); adds ?on_conflict=...
   * @returns {Promise<{data: any, error: any}>}
   */
  async insert(table, data, options = {}) {
    const { authToken: explicitToken, returnMinimal, ignoreDuplicates, upsert, onConflict, schema: explicitSchema } = options;
    const target = resolveWriteTarget('insert', table, explicitSchema, data);
    if (target.refused) return { data: null, error: DEMO_REFUSED };

    // Use user's JWT token if available for RLS, fall back to anon key — except for a demo-targeted
    // request, which is not sent at all without a usable session (see resolveRequestToken). An
    // explicitly-passed authToken is tested too: it is the credential that would actually be sent.
    const authToken = explicitToken || await this.getAuthToken();
    const credential = resolveRequestToken(target.schema, authToken);
    if (credential.expired) return { data: null, error: demoSessionExpiredError() };
    const token = credential.token;

    // Build Prefer header: combine return preference and duplicate resolution
    const preferParts = [];
    preferParts.push(returnMinimal ? 'return=minimal' : 'return=representation');
    if (upsert) {
      // Upsert: update the conflicting row(s) instead of erroring on a unique clash.
      preferParts.push('resolution=merge-duplicates');
    } else if (ignoreDuplicates) {
      preferParts.push('resolution=ignore-duplicates');
    }

    // For a conflict on a non-primary-key unique column, PostgREST needs the
    // conflict target named explicitly via ?on_conflict=<column>.
    const conflictQuery = onConflict ? `?on_conflict=${encodeURIComponent(onConflict)}` : '';

    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/${table}${conflictQuery}`,
        {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Prefer': preferParts.join(', '),
            ...(target.schema ? { 'Content-Profile': target.schema } : {})
          },
          body: JSON.stringify(Array.isArray(data) ? data : [data])
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        return { data: null, error: { message: errorText, status: response.status } };
      }

      if (returnMinimal) {
        return { data: null, error: null };
      }

      const result = await response.json();
      // A row just written to demo is demo content: it can be edited, replied to and opened in demo mode.
      if (target.schema === 'demo') recordDemoProvenance(table, result);
      return { data: Array.isArray(result) ? result[0] : result, error: null };
    } catch (error) {
      console.error('supabaseFetch insert error:', error);
      return { data: null, error: { message: error.message } };
    }
  },

  /**
   * Delete records from a table
   * @param {string} table - Table name
   * @param {object} options - Delete options
   * @param {object} options.eq - Equality filters { column: value }
   * @param {string} options.authToken - Optional auth token for RLS
   * @returns {Promise<{data: any, error: any}>}
   */
  async delete(table, options = {}) {
    const { eq = {}, authToken: explicitToken, schema: explicitSchema } = options;
    const target = resolveWriteTarget('delete', table, explicitSchema, null, eq);
    if (target.refused) return { data: null, error: DEMO_REFUSED };

    // Build URL params for filters
    const params = new URLSearchParams();
    Object.entries(eq).forEach(([key, value]) => {
      params.append(key, `eq.${value}`);
    });

    // Never issue a DELETE with zero filters — PostgREST would delete every row in the table.
    // (Found live 15 Aug 2026: RoadmapTab.jsx called delete('admin_roadmap', id) — a bare id
    // string as `options`, not { eq: { id } } — which silently destructured to zero filters
    // before this guard existed. Caller-side fixed 15 Aug 2026 (docs/briefs/2026-08-15-
    // roadmaptab-full-table-delete-hazard.md); ReputationTuningTab.jsx/SocialImagesTab.jsx have
    // the matching update() mistake and remain open in docs/reuse-backlog.md. This guard stays
    // as defence-in-depth regardless — it should never again be the only thing standing between
    // a click and an empty table.)
    if (params.toString() === '') {
      return { data: null, error: { message: 'delete() requires at least one eq condition' } };
    }

    // Use user's JWT token if available for RLS, fall back to anon key — except for a demo-targeted
    // request, which is not sent at all without a usable session (see resolveRequestToken). An
    // explicitly-passed authToken is tested too: it is the credential that would actually be sent.
    const authToken = explicitToken || await this.getAuthToken();
    const credential = resolveRequestToken(target.schema, authToken);
    if (credential.expired) return { data: null, error: demoSessionExpiredError() };
    const token = credential.token;

    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/${table}?${params.toString()}`,
        {
          method: 'DELETE',
          headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(target.schema ? { 'Content-Profile': target.schema } : {})
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        return { data: null, error: { message: errorText, status: response.status } };
      }

      return { data: true, error: null };
    } catch (error) {
      console.error('supabaseFetch delete error:', error);
      return { data: null, error: { message: error.message } };
    }
  },

  /**
   * List files in a Supabase storage bucket (direct fetch, bypasses JS client)
   * @param {string} bucket - Bucket name (e.g. 'hero-images')
   * @param {string} prefix - Path prefix (e.g. 'public/userId')
   * @param {object} options - List options
   * @param {number} options.limit - Max files to return (default 50)
   * @param {object} options.sortBy - Sort config { column, order }
   * @returns {Promise<{data: Array|null, error: any}>}
   */
  /**
   * Get a valid auth token, refreshing if expired
   * Reads from localStorage and auto-refreshes via Supabase GoTrue API
   * @returns {Promise<string|null>} - Valid access token or null
   */
  async getAuthToken() {
    try {
      const projectRef = SUPABASE_URL?.split('//')[1]?.split('.')[0];
      const storageKey = `sb-${projectRef}-auth-token`;

      // Read from authStorage (IndexedDB first, localStorage fallback)
      const raw = await authStorage.getItem(storageKey);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      const accessToken = parsed?.access_token;
      const refreshToken = parsed?.refresh_token;

      if (!accessToken) return null;

      // Check if token is expired by decoding JWT payload
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);

        // Proactive refresh margin. 150s, DELIBERATELY WIDER than auth-js's own
        // EXPIRY_MARGIN_MS (90s: AUTO_REFRESH_TICK_THRESHOLD 3 x AUTO_REFRESH_TICK_DURATION_MS
        // 30_000, @supabase/auth-js/dist/main/lib/constants.js:6-13). The wider margin is what
        // makes THIS path win the race: by the time the client's own lazy refresh threshold
        // opens at exp-90s, storage already holds a freshly rotated session, so the client's
        // ungated __loadSession refresh (GoTrueClient.js:1226) has nothing to refresh.
        // It NARROWS that path, it does not close it — see the comment on _refreshAuthToken
        // and docs/briefs/2026-09-25-single-token-refresher-ledger.md.
        if (payload.exp && payload.exp > now + 150) {
          return accessToken;
        }
      } catch (e) {
        // If we can't decode, try to use it anyway
        return accessToken;
      }

      // Token is expired or about to expire - refresh it
      if (!refreshToken) return accessToken;

      // Single-flight: if a refresh is already running, share it instead of starting
      // a second one. Concurrent refreshes with the same (rotating) refresh token make
      // the loser 400 and wipe the session; one shared refresh resolves all waiters,
      // and a genuine 400/401 still clears the session — but exactly once.
      if (refreshInFlight) {
        return refreshInFlight;
      }
      refreshInFlight = this._refreshAuthToken(storageKey, parsed, accessToken, refreshToken)
        .finally(() => { refreshInFlight = null; });
      return refreshInFlight;
    } catch (e) {
      console.warn('Error getting auth token:', e);
      return null;
    }
  },

  /**
   * Perform the actual refresh-token exchange. Extracted from getAuthToken so a single
   * in-flight refresh can be shared across concurrent callers (see refreshInFlight).
   *
   * 2026-09-25 (docs/briefs/2026-09-25-single-token-refresher.md, ruling 2): a REFUSED REFRESH IS
   * NOT PROOF THE SESSION IS DEAD. Before this build any 400/401 deleted the stored session without
   * reading the body, so a survivable rotation collision signed the member out. Observed live on
   * 15 Sep 2026: two concurrent POSTs to grant_type=refresh_token, both 400
   * `refresh_token_not_found` (docs/briefs/2026-09-15-registration-confirmation-flow-probe-RESULTS.md:99-102).
   *
   * The PRIMARY recovery is state-based, not string-based, and therefore does not depend on which
   * error-code vocabulary this GoTrue build emits: on any 400/401 we re-read storage, and if it now
   * holds a DIFFERENT usable access token then another refresher already rotated successfully and we
   * use its result. Code matching only decides what to do when storage has NOT moved.
   *
   * Returns:
   *   - the new access token on success
   *   - a token recovered from storage when another refresher won the race
   *   - the existing access token on 5xx/network errors, and on an UNRECOGNISED 400/401 code
   *     (refused is not dead — we keep the session and let a later call try again)
   *   - null, after clearing storage, only on a genuinely revoked/invalid session
   *   - null on a thrown error (matches the original outer catch)
   * Has its own try/catch so it never leaves a rejected promise cached in refreshInFlight.
   *
   * NOTE ON SCOPE. Ruling 1 sets autoRefreshToken: false on the supabase-js client, which stops its
   * 30s ticker, its visibility-change refresh and its boot recovery — but NOT its lazy refresh
   * inside __loadSession (GoTrueClient.js:1226 is ungated), which still clears on a non-retryable
   * error. That path is narrowed by the wider margin in getAuthToken and by this function's
   * recovery, and is not closed. See the ledger.
   *
   * @returns {Promise<string|null>}
   */
  async _refreshAuthToken(storageKey, parsed, accessToken, refreshToken) {
    // GoTrue error codes, from the library's own enumeration —
    // @supabase/auth-js/dist/main/lib/error-codes.d.ts (auth-js 2.89.0). [REPO] evidence: the code
    // NAMES are the library's; which one this project's GoTrue returns for a reuse-interval
    // collision is [NOT ESTABLISHED] (Supabase MCP Unauthorized, no live call available), which is
    // why the state check above runs first and why an unrecognised code never clears.
    //
    // RECOVERABLE — the refresh token was rotated or is unknown to the server, but the SESSION may
    // well be alive in storage because another refresher just replaced it.
    const RECOVERABLE_CODES = ['refresh_token_already_used', 'refresh_token_not_found'];
    // REVOKED — the session itself is gone or unusable. auth-js special-cases session_not_found
    // into AuthSessionMissingError (lib/fetch.js:60-65), which is its own evidence that the library
    // reads that code as "genuinely gone".
    const REVOKED_CODES = ['session_not_found', 'session_expired', 'bad_jwt', 'user_not_found', 'user_banned'];

    // Read the machine-readable code out of a GoTrue error body. auth-js reads `code` when the
    // response carries X-Supabase-Api-Version >= 2024-01-01 and `error_code` otherwise
    // (lib/fetch.js:33-43). We send that header on the request (below) so the shape is
    // deterministic, but we read BOTH because the header is a request hint, not a guarantee.
    const codeOf = (body) => {
      if (!body || typeof body !== 'object') return null;
      return (typeof body.code === 'string' && body.code)
        || (typeof body.error_code === 'string' && body.error_code)
        || null;
    };

    // One exchange. Returns { ok, status, body } and never throws on a non-2xx.
    const exchange = async (token) => {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_KEY,
          'Content-Type': 'application/json',
          // Pin the error-body shape to the versioned form (`code`), as auth-js does on every
          // auth request (lib/fetch.js:78-83, constant at lib/constants.js:22).
          'X-Supabase-Api-Version': '2024-01-01'
        },
        body: JSON.stringify({ refresh_token: token })
      });
      let body = null;
      try { body = await response.json(); } catch (e) { /* empty or non-JSON body */ }
      return { ok: response.ok, status: response.status, body };
    };

    // Persist a rotated session, preserving every other field already in storage.
    const save = async (session) => {
      const updatedData = {
        ...parsed,
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        // GoTrue MAY omit expires_at. auth-js never trusts it to be present and derives it from
        // expires_in (lib/fetch.js:121-131); we must do the same, because a session stored with
        // expires_at undefined reads as NEVER EXPIRING to the client
        // (GoTrueClient.js:1198 and :1906 both treat a missing expires_at as not-expired), which
        // would hand a dead JWT to every client-routed call indefinitely. Before this build the
        // client's own ticker hid that; ruling 1 removes the ticker.
        expires_at: session.expires_at
          ?? (session.expires_in ? Math.floor(Date.now() / 1000) + session.expires_in : undefined),
        expires_in: session.expires_in
      };
      await authStorage.setItem(storageKey, JSON.stringify(updatedData));
    };

    // Did another refresher already replace the session with a usable one?
    const recoverFromStorage = async () => {
      try {
        const raw = await authStorage.getItem(storageKey);
        if (!raw) return null;
        const stored = JSON.parse(raw);
        const candidate = stored?.access_token;
        if (candidate && candidate !== accessToken && isUsableAccessToken(candidate)) {
          return candidate;
        }
      } catch (e) { /* unreadable storage — fall through to the code-based path */ }
      return null;
    };

    try {
      console.log('Auth token expired, refreshing...');
      let attempt = await exchange(refreshToken);

      if (!attempt.ok && (attempt.status === 400 || attempt.status === 401)) {
        // 1. State first: another refresher may have won the race and stored a good session.
        const recovered = await recoverFromStorage();
        if (recovered) {
          console.log('Refresh refused, but storage holds a newer session — using it');
          return recovered;
        }

        const code = codeOf(attempt.body);

        // 2. Genuinely revoked → this is the one case that clears.
        if (code && REVOKED_CODES.includes(code)) {
          console.log('Clearing auth token — session revoked:', code);
          await authStorage.removeItem(storageKey);
          return null;
        }

        // 3. Rotated/unknown refresh token, and storage did not move → retry ONCE, inside this same
        //    in-flight promise so the retry never spawns a second concurrent refresh.
        if (!code || RECOVERABLE_CODES.includes(code)) {
          const latestRaw = await authStorage.getItem(storageKey);
          const latest = latestRaw ? JSON.parse(latestRaw) : null;
          const retryToken = latest?.refresh_token || refreshToken;
          attempt = await exchange(retryToken);

          if (!attempt.ok) {
            // One more look at storage — the winner may have landed during the retry.
            const late = await recoverFromStorage();
            if (late) {
              console.log('Retry refused, but storage holds a newer session — using it');
              return late;
            }
            if (attempt.status === 400 || attempt.status === 401) {
              console.log('Clearing auth token — refresh refused twice', code ? `(${code})` : '');
              await authStorage.removeItem(storageKey);
              return null;
            }
            // 5xx/network on the retry: keep what we have.
            console.warn('Failed to refresh auth token on retry:', attempt.status);
            return accessToken;
          }
        } else {
          // 4. A 400/401 we do not recognise is NOT proof of death. Keep the session.
          console.warn('Refresh refused with an unrecognised code, keeping session:', code);
          return accessToken;
        }
      } else if (!attempt.ok) {
        // 5xx/network — keep the token and let a later call retry.
        console.warn('Failed to refresh auth token:', attempt.status);
        return accessToken;
      }

      const newSession = attempt.body;
      if (!newSession?.access_token) {
        console.warn('Refresh returned no access token, keeping session');
        return accessToken;
      }
      await save(newSession);
      console.log('Auth token refreshed successfully');
      return newSession.access_token;
    } catch (e) {
      console.warn('Error refreshing auth token:', e);
      return null;
    }
  },

  /**
   * Upload a file to Supabase storage (direct fetch, bypasses JS client which hangs)
   * @param {string} bucket - Bucket name (e.g. 'user-uploads')
   * @param {string} path - File path within bucket (e.g. 'article-pictures/file.jpg')
   * @param {Blob|File} fileBody - File or Blob to upload
   * @param {object} options - Upload options
   * @param {string} options.contentType - MIME type (auto-detected from File if not provided)
   * @param {boolean} options.upsert - Whether to overwrite existing file (default false)
   * @returns {Promise<{data: {path: string}|null, error: any}>}
   */
  async storageUpload(bucket, path, fileBody, options = {}) {
    const { contentType = fileBody.type || 'application/octet-stream', upsert = false } = options;

    const authToken = await this.getAuthToken();
    const token = authToken || SUPABASE_KEY;

    try {
      const response = await fetch(
        `${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`,
        {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': contentType,
            'x-upsert': upsert ? 'true' : 'false'
          },
          body: fileBody
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        return { data: null, error: { message: errorText, status: response.status } };
      }

      return { data: { path }, error: null };
    } catch (error) {
      console.error('supabaseFetch storageUpload error:', error);
      return { data: null, error: { message: error.message } };
    }
  },

  async storageList(bucket, prefix, options = {}) {
    const { limit = 50, sortBy = { column: 'created_at', order: 'desc' } } = options;

    try {
      // Use user's JWT token if available, fall back to anon key
      const authToken = await this.getAuthToken();
      const token = authToken || SUPABASE_KEY;

      const response = await fetch(
        `${SUPABASE_URL}/storage/v1/object/list/${bucket}`,
        {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            prefix,
            limit,
            sortBy
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        return { data: null, error: { message: errorText, status: response.status } };
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      console.error('supabaseFetch storageList error:', error);
      return { data: null, error: { message: error.message } };
    }
  }
};

// Sample data creation utility (for development setup)
export const createSampleData = async () => {
  try {
    console.log('Creating sample data...');

    const { data: community } = await supabaseRaw
      .from('communities')
      .insert([{
        name: 'Aveiro Electric Car Enthusiasts',
        community_type: 'group',
        description: 'Discussing sustainable transport in Aveiro',
        postcode: '3810-193',
        slug: 'aveiro-electric-cars',
        is_private: false
      }])
      .select()
      .single();

    if (community) {
      await supabaseRaw.from('articles').insert([
        {
          title: 'New Tesla Charging Station Opens',
          content: 'Great news for electric vehicle owners in Aveiro!',
          article_type: 'news',
          community_id: community.id,
          location_postcode: '3810-193',
          is_public: true,
          summary: 'New EV charging infrastructure in Aveiro'
        }
      ]);
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
